import { FormControlLabel } from '@mui/material';
import { type ColDef } from 'ag-grid-community';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { ListWrapper } from 'components/AgGrid/CellRenderer/ListView.styled';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import { FlexBox } from 'components/Containers/FlexBox';
import * as React from 'react';
import { selectAssociatedDevices } from 'store/asset.slice';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { type Device } from 'types/device.types';
import { copyToClipboard } from 'utils/helpers/general';
import { DeviceInfoModal } from 'views/Settings/ExternalUsers/DeviceView/components/modal/DeviceInfoModal';
import {
  DeviceName,
  connectionDate,
  daysBehind,
  ingestDate,
  lastLocation,
  lastLocationTime,
  xOrientation,
  yOrientation,
  BumperNumberForTable,
} from './DeviceListCellRenderer';
import { AssetDetailModal } from './modal/AssetDetailModal';
import { WithIconRenderer } from 'components/AgGrid/CellRenderer/WithIconRenderer';
import { IconRenderer } from 'components/AgGrid/CellRenderer/IconRenderer';
import { DeviceUnlinkModal } from './modal/DeviceUnlinkModal';
import { selectLatestTagData } from 'store/device.slice';

import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { isExternalUser } from 'utils/validators/role.validation';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

enum OpenModal {
  None,
  EditAsset,
  ViewAsset,
  DeactivateAsset,
  DeviceInfo,
  DeviceUnlink,
}

/**
 * A component that displays a list of devices
 */
export const DeviceList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedDeviceId, setSelectedDeviceId] = React.useState('');
  const [modalOpen, setModalOpen] = React.useState(OpenModal.None);
  const [showUnassociatedDevices, setShowUnassociatedDevices] =
    React.useState<boolean>(false);

  const { assets } = useAppSelector((state) => state.assetReducer);
  // 1. devices that are not associated with an asset
  const { unassociatedDevices } = useAppSelector(
    (state) => state.deviceReducer
  );
  // 2. devices that are associated with an asset
  const associatedDevices = useAppSelector(selectAssociatedDevices);
  const latestTagData = useAppSelector(selectLatestTagData);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  const { customer, role } = useAppSelector((state) => state.authReducer);
  const theme = customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const isDisabled = isExternalUser(role);

  // 3. device list is composed of associated and unassociated devices

  const deviceList = React.useMemo(() => {
    const associatedWithLatestData = associatedDevices.map((device) => {
      const deviceLatestTags = latestTagData[`${device.deviceId}`];
      return {
        ...device,
        ...(deviceLatestTags || {}),
      };
    });
    return [...associatedWithLatestData, ...unassociatedDevices];
  }, [associatedDevices, unassociatedDevices, latestTagData]);

  // 4. Selected asset will open in the modal
  const selectedAsset = assets.find(
    (asset) => asset?.device?.deviceId === selectedDeviceId
  );
  // 5. Selected device will open in the modal
  const selectedDevice = deviceList.find(
    (device) => device.deviceId === selectedDeviceId
  );

  const hasLocationData = React.useMemo(
    () => deviceList.some((device) => device.last_location),
    [deviceList]
  );
  const hasLocationTsData = React.useMemo(
    () =>
      process.env.NODE_ENV === 'development' &&
      deviceList.some((device) => device.last_location_ts),
    [deviceList]
  );

  const columnDefs = React.useMemo<ColDef[]>(
    () => [
      {
        field: 'deviceId',
        headerName: 'Device',
        minWidth: 160,
        cellRenderer: DeviceName,
        cellRendererParams: {
          handleIdClick: (deviceId: string) => {
            if (isDisabled) return;
            setSelectedDeviceId(deviceId);
            setModalOpen(OpenModal.DeviceInfo);
          },
        },
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        field: 'assetBumperNumber',
        headerName: t('bumper_id_10'),
        minWidth: 160,
        filter: 'agTextColumnFilter',
        cellRenderer: BumperNumberForTable,
        cellRendererParams: {
          latestTagData,
          assetsWithLiveData,
        },
      },
      {
        field: 'assetId',
        headerName: t('asset_id'),
        cellRenderer: WithIconRenderer,
        cellRendererParams: {
          field: 'assetId',
          iconName: 'copyIcon',
          maxLength: 8,

          onIconClick: async (data: Device) => {
            const didCopy = await copyToClipboard(data.assetId);

            // Failed to copy to clipboard, return early
            if (!didCopy) return;

            // Show notification
            dispatch(
              showNotification({
                message: t('copied_to_clipboard'),
                type: 'success',
              })
            );
          },
          onCellClick: async (data: Device) => {
            setSelectedDeviceId(data.deviceId);
            setModalOpen(OpenModal.ViewAsset);
          },
        },
      },
      // {
      //   field: 'deviceStatus',
      //   cellRenderer: DeviceStatus,
      //   headerName: 'Device Status',
      //   minWidth: 150,
      // },
      {
        field: 'ingestDate',
        headerName: t('last_sensor_reading'),
        cellRenderer: ingestDate,
      },
      {
        field: 'connectionDate',
        headerName: t('last_connected_time'),
        cellRenderer: connectionDate,
      },
      {
        field: 'last_location',
        headerName: t('cdp_table_header_last_location'),
        cellRenderer: lastLocation,
        hide: !hasLocationData,
      },
      {
        field: 'last_location_ts',
        headerName: 'Last Location Time',
        cellRenderer: lastLocationTime,
        hide: !hasLocationTsData,
      },
      {
        field: 'daysBehind',
        headerName: t('days_behind'),
        cellStyle: { textAlign: 'center' },
        cellRenderer: daysBehind,
      },
      {
        field: 'xOrientation',
        headerName: 'xOrientation',
        cellRenderer: xOrientation,
      },
      {
        field: 'yOrientation',
        headerName: 'yOrientation',
        cellRenderer: yOrientation,
      },
      {
        field: 'deviceId',
        headerName: t('unlink_device_from_asset'),
        cellRenderer: IconRenderer,
        cellRendererParams: {
          renderCondition: ['assetId'],
          iconName: 'unlinkIcon',
          style: {
            display: 'flex',
            justifyContent: 'center',
          },
          onIconClick: async (data: Device) => {
            if (isDisabled) return;
            setSelectedDeviceId(data.deviceId);
            setModalOpen(OpenModal.DeviceUnlink);
          },
        },
        hide: isDisabled,
      },
    ],
    [
      isDisabled,
      latestTagData,
      assetsWithLiveData,
      hasLocationData,
      hasLocationTsData,
      dispatch,
    ]
  );

  return (
    <>
      {
        // Unink device confirmation modal
        modalOpen === OpenModal.DeviceUnlink && selectedDevice && (
          <DeviceUnlinkModal
            key={`${modalOpen.toString()}-unlink-device-modal`}
            deviceData={selectedDevice}
            open={modalOpen === OpenModal.DeviceUnlink}
            handleAbort={() => {
              setModalOpen(OpenModal.None);
            }}
          />
        )
      }
      {modalOpen === OpenModal.ViewAsset && selectedAsset && (
        <AssetDetailModal
          key={`${modalOpen.toString()}-edit-asset-modal`}
          open={modalOpen === OpenModal.ViewAsset}
          handleAbort={() => {
            setModalOpen(OpenModal.None);
          }}
          title={t('asset_detail')}
          assetData={selectedAsset}
        />
      )}

      {modalOpen === OpenModal.DeviceInfo && selectedDevice && (
        <DeviceInfoModal
          key={`${modalOpen.toString()}-device-info-modal}`}
          deviceData={selectedDevice}
          title={t('edit_device')}
          open={modalOpen === OpenModal.DeviceInfo}
          handleAbort={() => {
            setModalOpen(OpenModal.None);
          }}
        />
      )}
      <FlexBox
        sx={{
          display: 'flex',
          marginRight: '2rem',
          justifyContent: 'flex-end',
        }}
      >
        <FormControlLabel
          control={<StyledSwitchButton />}
          label={t('show_unlinked_devices')}
          labelPlacement="start"
          checked={showUnassociatedDevices}
          onChange={() => {
            // Flip the value
            setShowUnassociatedDevices((last) => !last);
          }}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
      </FlexBox>
      <ListWrapper>
        <BaseTableList
          rowData={deviceList}
          columnDefs={columnDefs}
          // filter is ON when NOT viewing unassociated devices
          // when it is ON, show unassociated devices
          isExternalFilterPresent={!showUnassociatedDevices}
          // filter on the assetId, if does not exist show unassociated devices
          filter={(node) => !!node.data?.assetId}
        />
      </ListWrapper>
    </>
  );
};
